import React, { useState, useCallback, useRef, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { useHttpClient } from "shared/hooks/http-hook";
import AreYouSureModal from "components/shared/AreYouSureModal";
import { AiChatContext } from "./AiChatContext";
import "./AiChatWidget.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/g;
const AI_CHAT_ERROR_KEYS = {
  AI_CHAT_THREAD_NOT_FOUND: "aiChat.errors.threadNotFound",
  AI_CHAT_BRAND_UNSUPPORTED: "aiChat.errors.brandUnsupported",
  AI_CHAT_BAD_REQUEST: "aiChat.errors.badRequest",
  AI_CHAT_UNAVAILABLE: "aiChat.errors.unavailable",
};

const AiChatWidget = () => {
  const { t } = useTranslation();
  const { sendRequest } = useHttpClient();
  const { open, setOpen } = useContext(AiChatContext);

  const [view, setView] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatThreadId, setChatThreadId] = useState(null);
  const [threads, setThreads] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }, []);

  const resolveAiChatError = useCallback(
    (err) => {
      const key = AI_CHAT_ERROR_KEYS[err?.message];
      return key ? t(key) : t("aiChat.errorMessage");
    },
    [t]
  );

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setLoading(true);
    scrollToBottom();

    try {
      const data = await sendRequest(
        `${BACKEND}/ai-chat/message`,
        "POST",
        JSON.stringify({ message: text, chatThreadId })
      );
      setChatThreadId(data.chatThreadId);
      setMessages((prev) => [...prev, { role: "assistant", text: data.reply }]);
    } catch (err) {
      const friendlyError = resolveAiChatError(err);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: friendlyError },
      ]);
    }
    setLoading(false);
    scrollToBottom();
  }, [input, loading, chatThreadId, sendRequest, scrollToBottom, resolveAiChatError]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const startNewChat = useCallback(() => {
    setChatThreadId(null);
    setMessages([]);
    setView("chat");
  }, []);

  const fetchThreads = useCallback(async () => {
    try {
      const data = await sendRequest(`${BACKEND}/ai-chat/threads`);
      setThreads(data.threads || []);
    } catch {
      setThreads([]);
    }
  }, [sendRequest]);

  const loadThread = useCallback(
    async (id) => {
      setChatThreadId(id);
      setView("chat");
      setLoading(true);
      try {
        const data = await sendRequest(`${BACKEND}/ai-chat/threads/${id}/messages`);
        setMessages(
          (data.messages || []).map((m) => ({
            role: m.role,
            text: m.text,
          }))
        );
      } catch (err) {
        const friendlyError = resolveAiChatError(err);
        setMessages([]);
        setMessages((prev) => [...prev, { role: "assistant", text: friendlyError }]);
      }
      setLoading(false);
      scrollToBottom();
    },
    [sendRequest, scrollToBottom, resolveAiChatError]
  );

  const handleDeleteThread = useCallback(
    (e, id) => {
      e.stopPropagation();
      setThreadToDelete(id);
      setShowDeleteModal(true);
    },
    []
  );

  const confirmDeleteThread = useCallback(
    async () => {
      if (!threadToDelete) return;
      try {
        await sendRequest(`${BACKEND}/ai-chat/threads/${threadToDelete}`, "DELETE");
        setThreads((prev) => prev.filter((thread) => thread._id !== threadToDelete));

        if (chatThreadId === threadToDelete) {
          setChatThreadId(null);
          setMessages([]);
          setView("threads");
        }
      } catch (err) {
        const friendlyError = resolveAiChatError(err);
        setMessages((prev) => [...prev, { role: "assistant", text: friendlyError }]);
      } finally {
        setShowDeleteModal(false);
        setThreadToDelete(null);
      }
    },
    [sendRequest, resolveAiChatError, chatThreadId, threadToDelete]
  );

  const showThreads = useCallback(() => {
    setView("threads");
    fetchThreads();
  }, [fetchThreads]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const renderMessageText = useCallback((text) => {
    if (!text) return null;

    const parts = text.split(URL_REGEX);
    return parts.map((part, index) => {
      if (/^https?:\/\//.test(part)) {
        return (
          <a key={`link-${index}`} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </a>
        );
      }
      return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    });
  }, []);

  const renderAssistantMarkdown = useCallback((text) => {
    if (!text) return null;

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    );
  }, []);

  if (!open) return null;

  return (
    <div className="ai-chat-panel">
      <div className="ai-chat-header">
        <span>{view === "threads" ? t("aiChat.conversations") : t("aiChat.help")}</span>
        <div className="ai-chat-header-actions">
          {view === "chat" && (
            <>
              <button onClick={startNewChat} title={t("aiChat.newChatTitle")}>+</button>
              <button onClick={showThreads} title={t("aiChat.historyTitle")}>&#9776;</button>
            </>
          )}
          {view === "threads" && (
            <button onClick={() => setView("chat")} title={t("aiChat.backTitle")}>&#8592;</button>
          )}
          <button onClick={() => setOpen(false)} title={t("aiChat.closeTitle")}>&#10005;</button>
        </div>
      </div>

      {view === "chat" && (
        <>
          <div className="ai-chat-messages">
            {messages.length === 0 && !loading && (
              <div style={{ textAlign: "center", color: "#999", marginTop: "40px", fontSize: "13px" }}>
                {t("aiChat.startMessage")}
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`ai-chat-msg ${msg.role}`}>
                {msg.role === "assistant" ? renderAssistantMarkdown(msg.text) : renderMessageText(msg.text)}
              </div>
            ))}
            {loading && <div className="ai-chat-typing">...</div>}
            <div ref={messagesEndRef} />
          </div>
          <div className="ai-chat-input-area">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("aiChat.askPlaceholder")}
              disabled={loading}
            />
            <button onClick={handleSend} disabled={loading || !input.trim()}>
              {t("send")}
            </button>
          </div>
        </>
      )}

      {view === "threads" && (
        <div className="ai-chat-thread-list">
          <div className="ai-chat-new-thread" onClick={startNewChat}>
            {t("aiChat.newConversation")}
          </div>
          {threads.map((thread) => (
            <div
              key={thread._id}
              className="ai-chat-thread-item"
              onClick={() => loadThread(thread._id)}
            >
              <span className="ai-chat-thread-title">{thread.title || t("aiChat.untitled")}</span>
              <span className="ai-chat-thread-date">
                {new Date(thread.updatedAt).toLocaleDateString()}
              </span>
              <button
                className="ai-chat-thread-delete"
                onClick={(e) => handleDeleteThread(e, thread._id)}
                title={t("aiChat.delete")}
              >
                &#128465;
              </button>
            </div>
          ))}
          {threads.length === 0 && (
            <div style={{ textAlign: "center", color: "#999", marginTop: "20px", fontSize: "13px" }}>
              {t("aiChat.noConversations")}
            </div>
          )}
        </div>
      )}
      <AreYouSureModal
        showModal={showDeleteModal}
        title={t("aiChat.deleteTitle")}
        text={t("aiChat.deleteConfirm")}
        onClose={() => {
          setShowDeleteModal(false);
          setThreadToDelete(null);
        }}
        onConfirmed={confirmDeleteThread}
      />
    </div>
  );
};

export default AiChatWidget;
